import React, { FC, useState } from "react";
import { useTheme } from "styled-components";
import { addPostRef, getCvScore } from "helpers/links-builders/loggedInLinks";
import { getJobPosterPostClientSide } from "dataLayer/apisWithCache/verticalApis/jobPostView";
import { useSsoContext } from "contexts/authSSOContext";
import {
  addListingAction,
  applyCvAction,
} from "../../../libs/logging/helpers/loggingActions";
import {
  EnumUxTypes,
  EnumWidgets,
} from "../../../libs/logging/types/loggingTypes";
import { IJobPosterPostView } from "models/jobPostView";
import { handleLoggingAuthenticate } from "../../../libs/logging/helpers/shareLogging";
import { linkToAddEditCv } from "../../../screens/seller-pages/_private/SellerPagesLinks";
import dynamic from "next/dynamic";

const CompleteCvPopUp = dynamic(
  () => import("componentScreens/JobsPostViewPages/common/CompleteCvPopUp"),
  { suspense: false }
);

const toastify = dynamic(
  () => import("../../UIComponents/StickyAlertMsg/snackbarAlert"),
  { suspense: false }
);

interface IRenderParams {
  onClickCb: (any) => void;
  applyLoader?: boolean;
  setApplyLoader?: (boolean?) => void;
}

interface IProps {
  render: ({ onClickCb }: IRenderParams) => JSX.Element;
  gtmAction?: () => void;
  iframeUrl?: string;
  jobApply?: boolean;
  postId?: number;
  cvScore?: number;
  isApplied?: boolean;
  widgetName?: string;
  postData?: IJobPosterPostView;
  editCv?: boolean;
}
interface IJobData {
  isApplied: boolean;
  cvScore: number;
}
export const AddPostProvider: FC<IProps> = ({
  render,
  iframeUrl,
  jobApply = false,
  cvScore: cvScoreDef,
  postId,
  isApplied: isAppliedDef,
  gtmAction,
  widgetName,
  postData,
  editCv = false,
}) => {
  const [openCompleteCvPop, setOpenCompleteCvPop] = useState(false);
  const [jobPosterData, setJobPosterData] = useState<IJobData | null>({
    isApplied: isAppliedDef,
    cvScore: cvScoreDef,
  });
  const [applyLoader, setApplyLoader] = useState<boolean>(false);
  const { isDesktop, device, locale, translations } = useTheme();
  const { isLoggedInSso, authenticate, verifiedToken } = useSsoContext();

  const reqUrl =
    typeof window !== "undefined" &&
    window.location.pathname.replace(`/${locale}`, "");

  const immediateCb = async (userInfo, getJobData) => {
    const isBlocked = userInfo?.isBlocked;
    //  1- if the user was blocked
    if (isBlocked) {
      // @ts-ignore
      toastify({
        severity: "success",
        title: translations.jobPostView.isBlockedMessage,
      });
    }

    //2- if the user logout
    if (getJobData && !isBlocked) {
      const postData = await getJobPosterPostClientSide(
        postId,
        device,
        locale,
        reqUrl
      );
      const isApplied = !postData.cta.applyForJob.enabled;
      const cvScore = await getCvScore();
      setJobPosterData({ isApplied: isApplied, cvScore: cvScore });
      return handleScoreCv(cvScore);
    }
    // 3- if the user already loggedIn
    return handleScoreCv(jobPosterData.cvScore);
  };

  const handleScoreCv = (score) => {
    if (jobPosterData.isApplied) return;
    if (score <= 50) {
      setOpenCompleteCvPop(true);
      setApplyLoader(false);
    } else {
      window.location.href = iframeUrl;
    }
    return;
  };

  const handleTokenVerification = async (response, getJobData) => {
    const userInfo = response?.userInfo;
    await immediateCb(userInfo, getJobData);
  };

  const onClickCb = async (evt) => {
    evt && evt.preventDefault();
    evt && evt.stopPropagation();
    gtmAction && gtmAction();
    if (jobApply) {
      applyCvAction({
        post: {
          ...postData,
          post_id: postData?.card?.post_id,
          member_id: postData?.card?.member_id,
          category_reporting_name: postData?.category_reporting_name,
          sub_category_reporting_name: postData?.sub_category_reporting_name,
          neighborhoodName: postData?.neighborhoodName,
          services: postData?.card?.services,
          member_isShop: postData?.member_isShop,
        },
      });
      if (applyLoader) return;
      isLoggedInSso
        ? await verifiedToken().then((res) => {
            handleTokenVerification(res, false);
          })
        : await verifiedToken((res) => {
            setApplyLoader(true);
            handleTokenVerification(res, true);
          }).then();

      addPostRef();
    } else if (editCv) {
      if (!isLoggedInSso) {
        verifiedToken(() => {
          window.location.href = linkToAddEditCv(device);
        }).then(addPostRef);
      } else {
        window.location.href = linkToAddEditCv(device);
        addPostRef();
      }
    } else {
      if (!isLoggedInSso) {
        addListingAction({
          post: {
            ...postData,
            post_id: postData?.card?.post_id,
            member_id: postData?.card?.member_id,
            category_reporting_name: postData?.category_reporting_name,
            sub_category_reporting_name: postData?.sub_category_reporting_name,
            neighborhoodName: postData?.neighborhoodName,
            services: postData?.card?.services,
            member_isShop: postData?.member_isShop,
          },
          widgetName: widgetName as EnumWidgets,
        });
        verifiedToken(
          () =>
            (window.location.href =
              process.env.NEXT_PUBLIC_ADD_POST_TEMPLATE_URL)
        ).then(addPostRef);
      } else {
        window.location.href = process.env.NEXT_PUBLIC_ADD_POST_TEMPLATE_URL;
      }
    }
    if (!isLoggedInSso) {
      handleLoggingAuthenticate({
        currentWidgetName: widgetName as EnumWidgets,
        ux_type: EnumUxTypes.BUTTON,
      });
    }
  };

  return (
    <>
      {render({ onClickCb, applyLoader, setApplyLoader })}
      {openCompleteCvPop && (
        <CompleteCvPopUp
          isOpen={openCompleteCvPop}
          cancelAction={() => {
            setOpenCompleteCvPop(false);
            setApplyLoader(false);
          }}
          ConformationAction={() => {
            setOpenCompleteCvPop(false);
            window.location.href = iframeUrl;
          }}
          isDesktop={isDesktop}
        />
      )}
    </>
  );
};
